import { FC, Fragment, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { PDFExport } from '@progress/kendo-react-pdf';
import { drawDOM, pdf, exportPDF } from '@progress/kendo-drawing';
import { CanceledError } from 'axios';

import { Project, ReportApiData, User } from '../../utils/models';
import { getCanCreateReport, saveImage, saveJSONFile } from '../../utils/utilities';
import { HTTP_SUCCESS_CODES } from '../../utils/constants';
import { store, useAppDispatch, useAppSelector } from '../../store/configureStore';

import { getReportDetails, setBase64, setConfirmExcelIsoUpload, setConfirmReportPublish, setExcelDocumentUrl, setPublishing, setSaving, setUploadingExcel } from '../../store/reportDetails';
import { createReport, saveReportProgress, updateReport } from '../../services/reportService';
import { getCurrentUser } from '../../services/authService';
import { base64Upload } from '../../services/fileUpload';
import { pdfOptions as defaultPDFOptions } from '../blocks/pdf-export';

import Button from '../themed/button/index';
import Conditional from '../common/conditional';
import Confirm from '../themed/confirm';
import ExcelISODocument from '../forms/custom-fvi/excelIsoUpload';
import LoadingBadge from '../themed/loading-badge';

import RiskRatingContext from '../../context/useRiskRatingContext';

import useCachedReport from '../../hooks/useCachedReport';
import useProgress from '../../hooks/useProgress';

import classNames from '../../components/themed/pdf/pdf.module.scss';
import config from '../../utils/config';
import routes from '../../utils/routes';
import logger from '../../services/logService';
import toastService from '../../services/toastService';

pdf.defineFont({
  'Allison': '/fonts/Allison-Regular.ttf',
});

interface PageTemplateProps {
	pageNum: number;
	totalPages: number;
}

function withPDF(Component: any): FC<any> {
	return function WithPDF(props) {
		const pdfExportComponent = useRef<PDFExport>(null);
		const elementRef = useRef<HTMLDivElement>(null);

		const riskContext = useContext(RiskRatingContext);
		const cachedReport = useCachedReport(props);

		const dispatch = useAppDispatch();
		const progress = useProgress();
		const reportDetails = useAppSelector(getReportDetails);

		const user = getCurrentUser() as User;
		const details: Project | null = cachedReport.projectDetails;

		const pdfOptions = { ...defaultPDFOptions, forcePageBreak: '.pdf-page' };

		const isAllowedToPublish = useMemo(() => {
			return getCanCreateReport(details, user);
		}, [details, user]);

		const getImageUrl = async (): Promise<string | null> => {
			if (!elementRef.current) return null;

			if (!reportDetails.base64) {
				const group = await drawDOM(elementRef.current, pdfOptions);
				const imageString: string = await exportPDF(group, pdfOptions);
				
				dispatch(setBase64(imageString));
			}

			const base64String = store.getState().reportDetails.base64;
			const url = await base64Upload((details as Project).documentNumber, base64String);

			return url;
		};

		const getExportPageTemplate = (props: PageTemplateProps) => {
			return (
				<div className={classNames['page-number']}>
					{props.pageNum + ' of ' + props.totalPages}
				</div>
			);
		};

		const downloadPdf = async () => {
			const pdf: PDFExport | null = pdfExportComponent.current;
			if (!pdf) return toastService.error('Document not available');

			dispatch(setSaving(true));
			pdf.save(() => dispatch(setSaving(false)));
		};

		const handleApiUpdate = async () => {
			if (!details) return;

			const url: string | null = await getImageUrl();
			if (!url) return;

			await saveImage(url, details.documentNumber);

			const apiData: ReportApiData = { projectId: details._id, reportUrl: url, riskRating: riskContext.riskRating };
			if (reportDetails.excelDocumentUrl) apiData.excelDocumentUrl = reportDetails.excelDocumentUrl;

			const isPublished: boolean = details.report?.isPublished;
			if (!isPublished) return await createReport(apiData, progress.onProgress);

			const reportId: string = details.report?._id;
			return await updateReport(reportId, apiData, progress.onProgress);

		};

		const handleToggleConfirmReportPublish = () => {
			dispatch(setConfirmReportPublish(!reportDetails.confirmReportPublish));
		};

		const handleToggleConfirmExcelIsoUpload = () => {
			if (config.uploadExcelDocument) {
				if (!details) return toastService.error('Fetching project details please wait!');

				const supportedTemplates: any = config.supportedTemplates;
				const templates = Object.keys(supportedTemplates).map((key) => (supportedTemplates)[key])
				
				if (templates.includes(details.inspectionTypeId)) {
					return dispatch(setConfirmExcelIsoUpload(!reportDetails.confirmExcelIsoUpload));
				}
			}

			handleToggleConfirmReportPublish();
		};

		const handleToggleExcelUploading = () => {
			dispatch(setUploadingExcel(!reportDetails.isUploadingExcel));
		};

		const handleDownloadReportData = useCallback(() => {
			if (details) {
				return saveJSONFile(cachedReport, details.documentNumber);
			}

			toastService.error('No Report Progress Found!');
		}, [cachedReport, details]);

		const handleSaveExcelUrl = (excelDocumentUrl: string) => {
			dispatch(setExcelDocumentUrl(excelDocumentUrl));

			handleToggleExcelUploading();
			handleToggleConfirmExcelIsoUpload();
			handleToggleConfirmReportPublish();
		};

		const handleCancelExcelUpload = () => {
			handleToggleConfirmExcelIsoUpload();
			handleToggleConfirmReportPublish();
		};

		const handleResetState = () => {
			dispatch(setConfirmExcelIsoUpload(false));
			dispatch(setUploadingExcel(false));
			dispatch(setConfirmReportPublish(false));
			dispatch(setPublishing(false));
			dispatch(setSaving(false));
			dispatch(setBase64(''));
		};

		const handlePublishReport = async () => {
			if (!details) return;

			try {
				dispatch(setPublishing(true));

				const response = await handleApiUpdate();
				dispatch(setPublishing(false));
				
				if (response && HTTP_SUCCESS_CODES.includes(response.status)) {
					handleResetState();
					window.location.href = routes.projects.fullPath;
				}
			} catch (ex: any) {
				dispatch(setPublishing(false));

				logger.log(ex);
			}
		};

		useEffect(() => {
			const controller = new AbortController();

			const handleProgressUpload = async () => {
				if (!details || !isAllowedToPublish) return;

				try {
					dispatch(setSaving(true));

					const savedProgressUrl: string | null = await getImageUrl();
					if (savedProgressUrl) {
						const apiData = { projectId: details._id, savedProgressUrl };
						await saveReportProgress(apiData, controller.signal);
					}

					dispatch(setSaving(false));
				} catch (error) {
					dispatch(setSaving(false));

					if (error instanceof CanceledError) return;
					toastService.error('Report progress not saved!');
				}
			};

			const timeout = setTimeout(() => {
				if (config.saveLivePreview) handleProgressUpload();
			}, 3_000);

			return () => {
				clearTimeout(timeout);
				controller.abort();
				handleResetState();
			};
		}, [dispatch, isAllowedToPublish]);

		return (
			<Fragment>
				<Conditional isVisible={reportDetails.isSaving}>
					<div className="p-3 bg-white border-bottom d-flex justify-content-end sticky-top">
						<LoadingBadge message="Loading..." />
					</div>
				</Conditional>

				<PDFExport
					{...pdfOptions}
					author="Arridex"
					subject={details?.documentNumber}
					title={details?.projectTitle}
					fileName={details?.documentNumber + '.pdf'}
					producer="RSMG"
					ref={pdfExportComponent}
					pageTemplate={getExportPageTemplate}
				>
					<div className={classNames['pdf-draft-page']} />

					<div ref={elementRef}>
						<Component {...cachedReport} onDownload={downloadPdf} />
					</div>
				</PDFExport>

				<Conditional isVisible={!reportDetails.isSaving}>
					<div className="sticky-bottom p-3 bg-white border-top">
						<Conditional isVisible={reportDetails.confirmExcelIsoUpload}>
							<Fragment>
								{reportDetails.isUploadingExcel ? (
									<ExcelISODocument onAdd={handleSaveExcelUrl} onCancel={handleToggleExcelUploading} /> 
								) : (
									<Confirm
										title="Upload Anomaly location or ISO or Drawing Document"
										message="Do you have the Anomaly location or ISO or Drawing document and would like to upload it?"
										onConfirm={handleToggleExcelUploading}
										onCancel={details?.report?.excelDocumentUrl || user.role.reviewReports ? handleCancelExcelUpload : undefined}
									/>
								)}
							</Fragment>
						</Conditional>

						<Conditional isVisible={reportDetails.confirmReportPublish}>
							<Fragment>
								{reportDetails.isPublishing ? (
									<LoadingBadge message="Publishing Report" />
								) : (
									<Confirm
										title="Confirm Upload"
										message="Do you want to upload the report for review? Ensure the details you entered are valid. Tap the YES button below to confirm"
										onCancel={handleToggleConfirmReportPublish}
										onConfirm={handlePublishReport}
									/>
								)}
							</Fragment>
						</Conditional>

						<div className='d-flex justify-content-end'>
							<Conditional isVisible={!reportDetails.confirmReportPublish && !reportDetails.confirmExcelIsoUpload}>
								<Conditional isVisible={isAllowedToPublish}>
									<Button
										label="Finish & Send for Review"
										icon="upload"
										style={{ marginTop: 0 }}
										disabled={reportDetails.isPublishing}
										onClick={handleToggleConfirmExcelIsoUpload}
									/>
								</Conditional>

								<Button
									label="PDF Preview"
									icon="download"
									style={{
										marginTop: 0,
										marginLeft: '1rem',
										marginRight: '1rem',
										backgroundColor: '#ab3c3c',
									}}
									onClick={downloadPdf}
								/>

								<Conditional isVisible={process.env.NODE_ENV === 'development'}>
									<Button
										label="Download Raw Report Data"
										icon="download"
										style={{ marginTop: 0, backgroundColor: '#000000' }}
										onClick={handleDownloadReportData}
									/>
								</Conditional>
							</Conditional>
						</div>
					</div>
				</Conditional>
			</Fragment>
		);
	};
}

export default withPDF;
